import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { formatDate } from "@/utils/formatters";

export type CoverageAuditDialogStep = "confirm" | "starting" | "started";
export type CoverageAuditKind = "page" | "journey";

const PROBING_HELP =
  "Explore any interaction, including forms and signups. Off: navigate and browse only, attempting to skip actions with side effects.";

type Props = {
  open: boolean;
  step: CoverageAuditDialogStep;
  kind?: CoverageAuditKind;
  label: string | null;
  alreadyInFlight?: boolean;
  fullApplicationProbing?: boolean;
  auditsUsed: number;
  auditLimit: number;
  auditsRefreshAt: string | null;
  onClose: () => void;
  onConfirm: () => void;
  onFullApplicationProbingChange?: (fullApplicationProbing: boolean) => void;
  onWatchLive: () => void;
};

export default function CoverageAuditDialog({
  open,
  step,
  kind = "page",
  label: subjectLabel,
  alreadyInFlight = false,
  fullApplicationProbing = false,
  auditsUsed,
  auditLimit,
  auditsRefreshAt,
  onClose,
  onConfirm,
  onFullApplicationProbingChange,
  onWatchLive,
}: Props) {
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null);
  const busy = step === "starting";
  const started = step === "started";

  useEffect(() => {
    setOverlayRoot(document.getElementById("layout-main-overlay"));
  }, []);

  useEffect(() => {
    if (!open || busy) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, busy, onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const main = document.querySelector("main[scroll-region]");
    if (!(main instanceof HTMLElement)) {
      return;
    }

    const previousOverflow = main.style.overflowY;
    main.style.overflowY = "hidden";

    return () => {
      main.style.overflowY = previousOverflow;
    };
  }, [open]);

  if (!open || !overlayRoot) {
    return null;
  }

  const fallbackLabel = kind === "journey" ? "this journey" : "this page";
  const label = subjectLabel?.trim() || fallbackLabel;
  const title = started
    ? alreadyInFlight
      ? "Audit already running"
      : "Audit started"
    : kind === "journey"
      ? "Audit this journey?"
      : "Audit this page?";
  const auditUsageSentence = auditsRefreshAt
    ? `This uses 1 page audit from your plan. You already used ${auditsUsed} out of ${auditLimit} monthly page audits and the quota resets on ${formatDate(
        auditsRefreshAt,
      )}.`
    : `This uses 1 page audit from your plan. You already used ${auditsUsed} out of ${auditLimit} page audits.`;
  const showProbing = kind === "page" && onFullApplicationProbingChange != null;

  return createPortal(
    <div
      className="pointer-events-auto absolute inset-0 z-50 flex items-center justify-center overflow-y-auto p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coverage-audit-dialog-title"
      aria-busy={busy}
    >
      <div className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm" aria-hidden="true" />
      <div className="relative my-auto w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
          <div>
            <h2 id="coverage-audit-dialog-title" className="text-lg font-semibold tracking-tight text-gray-900">
              {title}
            </h2>
            {started ? (
              <p className="mt-1 text-sm leading-relaxed text-gray-600">
                {alreadyInFlight
                  ? `We're already evaluating ${label}. You can leave and come back later — this list and the player will show progress and results.`
                  : `We're evaluating ${label} now. You can leave and come back later — this list and the player will show progress and results.`}
              </p>
            ) : (
              <div className="space-y-1 text-sm leading-relaxed text-gray-600">
                <p className="mt-2">
                  We&apos;ll {kind === "journey" ? "walk" : "browse"} <span className="font-semibold">{label}</span>{" "}
                  simulating a real user.
                </p>
                <p>{auditUsageSentence}</p>
                {showProbing ? (
                  <label className="mt-3 flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={fullApplicationProbing}
                      disabled={busy}
                      onChange={(event) => onFullApplicationProbingChange?.(event.target.checked)}
                    />
                    <span>
                      <span className="block text-[0.95rem] font-semibold text-gray-900">
                        Allow full application probing
                      </span>
                      <span className="mt-1.5 block text-[0.8125rem] leading-relaxed text-gray-600">
                        {PROBING_HELP}
                      </span>
                    </span>
                  </label>
                ) : null}
              </div>
            )}
          </div>
          <button
            type="button"
            className="shrink-0 cursor-pointer text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col-reverse gap-2 px-6 py-4 sm:flex-row sm:justify-end">
          {started ? (
            <>
              <button type="button" className="btn justify-center" onClick={onClose}>
                Dismiss
              </button>
              <button type="button" className="btn btn-primary justify-center" onClick={onWatchLive}>
                Watch live
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn justify-center" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary justify-center" onClick={onConfirm} disabled={busy}>
                {busy ? "Starting…" : "Confirm"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    overlayRoot,
  );
}
